import { Injectable, NgZone } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  User,
  onAuthStateChanged
} from '@angular/fire/auth';
import { CookieService } from 'ngx-cookie-service';
import { GoService } from './go.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

// --- Imports for Presence System ---
import { Database, ref, set, onDisconnect, serverTimestamp } from '@angular/fire/database';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(
    null,
  );

  constructor(
    private auth: Auth,
    private go: GoService,
    private cookieSv: CookieService,
    private toastr: ToastrService,
    private zone: NgZone,
    private db: Database
  ) {
    onAuthStateChanged(this.auth, (currentUser) => {
      this.zone.run(() => {
        this.userSubject.next(currentUser);
        this.updateUserPresence(currentUser);
      });
    });
  }

  private async updateUserPresence(user: User | null) {
    if (!user) {
      return;
    }
    // The reference to the Firestore document is no longer needed here for presence.
    const presenceRef = ref(this.db, `status/${user.uid}`);

    try {
      // No longer writing `online: true` to Firestore.
      // await setDoc(userDocRef, { online: true }, { merge: true }); // <<< REMOVED

      await onDisconnect(presenceRef).set({
        online: false,
        timestamp: serverTimestamp(),
      });

      await set(presenceRef, {
        online: true,
        timestamp: serverTimestamp(),
      });

    } catch (error) {
      console.error('Error setting up user presence:', error);
    }
  }

  get userUid(): string {
    return this.userSubject.value?.uid ?? '';
  }

  get currentUser(): User | null {
    return this.userSubject.value;
  }

  get user$(): Observable<User | null> {
    return this.userSubject.asObservable();
  }

  async signInWithEmail(email: string, password: string, isSave?: boolean) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      this.cookieManagement(email, password, isSave);
      this.go.home();
    } catch (error) {
      this.showFailed(error);
    }
  }

  private cookieManagement(email: string, password: string, isSave?: boolean) {
    if (isSave) {
      this.cookieSv.set('email', email);
      this.cookieSv.set('password', password);
    } else {
      this.cookieSv.delete('email');
      this.cookieSv.delete('password');
    }
  }

  async signOut() {
    const userUid = this.userUid;
    if (userUid) {
      const presenceRef = ref(this.db, `status/${userUid}`);
      // No longer writing `online: false` to Firestore.
      // await setDoc(userDocRef, { online: false }, { merge: true }); // <<< REMOVED
      
      // Explicitly set user to offline in Realtime Database on sign out.
      await set(presenceRef, { 
        online: false,
        timestamp: serverTimestamp(),
      });
    }

    try {
      await this.auth.signOut();
      this.cookieSv.delete('email');
      this.cookieSv.delete('password');
      this.go.login();
    } catch (error) {
      console.error('[AuthService]', 'Sign out failed', error);
    }
  }

  private showFailed(error: any) {
    const code = error?.code ?? 'NO_CODE_FOUND';
    console.error('[AuthService]', 'Toàn bộ lỗi:', error);

    let message = 'Đăng nhập thất bại!';
    let suggest = 'Vui lòng thử lại bằng tài khoản khác.';

    if (code === 'auth/invalid-credential') {
      message = 'Sai email hoặc mật khẩu!';
      suggest = 'Hãy kiểm tra lại thông tin đăng nhập.';
    } else if (code === 'auth/user-not-found') {
      message = 'Email này không có trong hệ thống!';
      suggest = 'Hãy sử dụng một email khác hoặc đăng ký mới.';
    } else if (code === 'auth/popup-closed-by-user') {
      message = 'Cửa sổ đăng nhập đã bị đóng khi chưa hoàn thành đăng nhập.';
      suggest = 'Hãy thử lại hoặc dùng tài khoản khác.';
    }

    this.toastr.error(suggest, message);
  }
}
